"use client";

import { Consultation } from "@/app/_components/consultation-card";
import { Error } from "@/app/_components/error";
import { Loading } from "../loading";
import { SearchLink } from "../search-link";
import { ExOwnerInfo } from "../ex-owner-info";

import { useAnalyze } from "@/queries/use-analyze";

import type { DiligenceItem } from "../diligence.types";

const urls = {
  google: {
    url: "https://www.google.com/search?q={query}",
    label: "Consultar serviço online",
    icon: "/logos/google-logo.svg",
  },
  registradores: {
    url: "https://registradores.onr.org.br/",
    label: "registradores.onr.org.br",
    icon: "/logos/registradores-onr-logo.svg",
  },
};

const createSearchUrl = (baseUrl: string, query: string) => {
  return baseUrl.replace("{query}", encodeURIComponent(query || ""));
};

export const Documentos = ({ id, field, emptyElement }: DiligenceItem) => {
  const { data, isPending, error, refetch } = useAnalyze({ id, field });

  if (isPending) return <Loading />;

  if (error) return <Error reset={refetch} />;

  const hasValidData = data?.analise?.data && data?.propriedade;

  if (!hasValidData) {
    return emptyElement ?? <p>Não há dados válidos para exibir.</p>;
  }

  const analyzeData = data?.analise?.data;
  const property = data?.propriedade;
  const currentOwner =
    analyzeData.moradores && analyzeData.moradores.length > 0
      ? analyzeData.moradores[0]
      : null;

  const uf = property?.uf || "";
  const city = property.cidade_descricao || "";
  const inscricaoImobiliaria = property?.inscricao_imobiliaria || "";

  const onusReaisSearchUrl = createSearchUrl(
    urls.google.url,
    `certidão ônus reais cartório registro imóveis ${city} ${uf}`
  );
  const certidaoNegativaSearchUrl = createSearchUrl(
    urls.google.url,
    `certidão negativa débitos imobiliários ${city} ${uf}`
  );

  return (
    <div className="flex flex-col gap-8">
      <ExOwnerInfo
        currentOwner={currentOwner}
        analyzeData={analyzeData}
        propertyNumber={inscricaoImobiliaria}
      />
      <Consultation.Card
        title="Certidão de Ônus Reais"
        description="A certidão de ônus reais é um documento que atesta a existência ou inexistência de ônus (restrições) sobre um imóvel, como hipotecas, penhoras ou outras dívidas."
      >
        <p className="text-sm text-muted-foreground">
          É importante para compradores e vendedores, pois revela se há alguma
          pendência que possa afetar a transação.
        </p>
        <SearchLink
          url={onusReaisSearchUrl}
          label="Consultar Certidão de Ônus Reais"
          icon={urls.google.icon}
        />
      </Consultation.Card>
      <Consultation.Card
        title="Certidão Negativa de Débitos"
        description="A Certidão Negativa de Débitos (CND) é um documento que comprova que uma pessoa física ou jurídica não possui pendências financeiras ou tributárias com um determinado órgão público."
      >
        <p className="text-sm text-muted-foreground">
          É como uma &quot;nota de aprovação&quot; que atesta a regularidade
          fiscal de alguém perante a Fazenda Nacional.
        </p>
        <SearchLink
          url={certidaoNegativaSearchUrl}
          label="Consultar Certidão Negativa de Débitos"
          icon={urls.google.icon}
        />
      </Consultation.Card>
      <Consultation.Card
        title="Matrícula"
        description="Baixar uma matrícula atualizada alguns dias antes do leilão é uma boa ideia para se precaver de averbações inesperadas, como indisponibilidades ou penhoras."
      >
        <p className="text-sm text-muted-foreground">
          Para isso, copie o número da matrícula, protocole um novo pedido de
          &quot;Visualização de Matrícula&quot; destinado ao cartório
          responsável e aguarde alguns minutos.
        </p>
        <SearchLink
          url={urls.registradores.url}
          label={urls.registradores.label}
          icon={urls.registradores.icon}
        />
        <p className="text-sm text-muted-foreground">
          Com o resultado em mãos, procure por gravames/ônus em averbações
          recentes.
        </p>
      </Consultation.Card>
      <Consultation.Card
        title="Intimações"
        description="A ausência de intimação para a purgação da mora é motivo suficiente para a anulação de um leilão. Portanto, certificar-se de que isso foi realizado corretamente é uma boa ideia."
      >
        <p className="text-sm text-muted-foreground">
          Para isso, acesse a matrícula e encontre a averbação de
          &quot;Consolidação da Propriedade&quot;. Se houver, procure também
          pela de &quot;Leilões Negativos&quot; ― anote os números dos atos.
        </p>
        <p className="text-sm text-muted-foreground">
          Em seguida, protocole um pedido de &quot;Certidão Digital&quot;
          destinado ao cartório responsável. Escolha &quot;Documento
          Arquivado&quot; e cole o número de cada ato.
        </p>
        <SearchLink
          url={urls.registradores.url}
          label={urls.registradores.label}
          icon={urls.registradores.icon}
        />
        <p className="text-sm text-muted-foreground">
          O retorno pode demorar alguns dias. Quando estiver com o resultado em
          mãos, verifique se o procedimento da intimação foi realizado
          corretamente.
        </p>
      </Consultation.Card>
    </div>
  );
};
